import BaseExportStrategy from './BaseExportStrategy';

/**
 * Estrategia de exportación para reportes de Info Cobranzas
 * Define las columnas y el formato específico con columnas dinámicas de períodos
 */
export default class InfoCobranzasExportStrategy extends BaseExportStrategy {
  constructor() {
    super('Info Cobranzas');
  }

  getTitle(context) {
    return 'INFORME DE COBRANZAS';
  }

  /**
   * Define las columnas del reporte de Info Cobranzas
   */
  getColumns(options = {}) {
    const columns = [];

    // 1. Legajo
    columns.push({
      key: 'legajo',
      header: 'Legajo',
      pdfWidth: 15,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.legajo || '-',

      formatSubtotal: (item, ctx) => 'SUBTOTAL',

      formatTotal: (items, ctx) => ''
    });

    // 2. Fideicomiso
    columns.push({
      key: 'fideicomiso',
      header: 'Fideicomiso',
      pdfWidth: 30,
      excelWidth: 30,
      align: 'left',

      format: (item, ctx) => item.fideicomiso || item.fideicomisoName || '-',

      formatSubtotal: (item, ctx) => `${item._cantidadRegistros || 0} registros`,

      formatTotal: (items, ctx) => 'TOTAL - RESUMEN'
    });

    // 3. Operatoria / Línea
    columns.push({
      key: 'operatoria_linea',
      header: 'Operatoria / Línea',
      pdfWidth: 38,
      excelWidth: 35,
      align: 'left',

      format: (item, ctx) => item.operatoria_linea || '-',

      formatSubtotal: (item, ctx) => {
        return (item._subtotales && item._subtotales.operatoria) || item.operatoria_linea || '-';
      },

      formatTotal: (items, ctx) => ''
    });

    // 4. Apellido y Nombre / Razón Social
    columns.push({
      key: 'nombre_razon_social',
      header: 'Apellido y Nombre / Razón Social',
      pdfWidth: 40,
      excelWidth: 40,
      align: 'left',

      format: (item, ctx) => {
        if (!item.nombre_razon_social) return '-';
        return item.nombre_razon_social.toUpperCase();
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 5. CUIT
    columns.push({
      key: 'cuit',
      header: 'CUIT',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => item.cuit || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 6. Departamento
    columns.push({
      key: 'departamento',
      header: 'Departamento',
      pdfWidth: 25,
      excelWidth: 20,
      align: 'left',

      format: (item, ctx) => {
        // ✅ PRIMERO: Verificar si el departamento ya viene del backend
        if (item.departamento && item.departamento !== '') {
          return item.departamento;
        }
        // Intentar obtener departamento desde diferentes estructuras
        if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
          return item.localidad.departamento.nombre;
        }
        if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
          return item.cliente.localidad.departamento.nombre;
        }
        // Fallback a localidad si no hay departamento
        if (item.cliente && item.cliente.localidad) {
          return item.cliente.localidad;
        }
        if (item.localidad && typeof item.localidad === 'string') {
          return item.localidad;
        }
        if (item.localidad && item.localidad.nombre) {
          return item.localidad.nombre;
        }
        return '-';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 7. Persona
    columns.push({
      key: 'persona',
      header: 'Persona',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.persona || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 8. Fecha de Ingreso
    columns.push({
      key: 'fecha_ingreso',
      header: 'Fecha de Ingreso',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => this.formatDates(item.fecha_ingreso),

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 9. Estado
    columns.push({
      key: 'estado',
      header: 'Estado',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.estado || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 10. Estado Manual
    columns.push({
      key: 'estado_manual',
      header: 'Estado Manual',
      pdfWidth: 20,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => item.estado_manual || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 11. Período
    columns.push({
      key: 'periodo',
      header: 'Período',
      pdfWidth: 22,
      excelWidth: 16,
      align: 'right',

      format: (item, ctx) => {
        const value = parseFloat(item.periodo) || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.periodo) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const periodo = parseFloat(item.periodo) || 0;
          return total + periodo;
        }, 0);
      }
    });

    // 12-N. Columnas dinámicas de períodos (meses)
    const columnasPeriodos = this.getColumnasPeriodos(options);
    console.log(`📊 Info Cobranzas - Generando ${columnasPeriodos.length} columnas de períodos`);

    columnasPeriodos.forEach(columnaPeriodo => {
      columns.push({
        key: columnaPeriodo.value,
        header: columnaPeriodo.text,
        pdfWidth: 20,
        excelWidth: 14,
        align: 'right',

        format: (item, ctx) => {
          const value = this.getMontoPorPeriodo(item, columnaPeriodo.periodo);
          return this.formatMonto(value);
        },

        formatSubtotal: (item, ctx) => {
          const value = this.getSubtotalParaPeriodo(item, columnaPeriodo.periodo);
          return this.formatMonto(value);
        },

        calculateTotal: (items, ctx) => {
          return items.reduce((total, item) => {
            const valor = this.getMontoPorPeriodo(item, columnaPeriodo.periodo);
            return total + valor;
          }, 0);
        }
      });
    });

    return columns;
  }

  /**
   * Obtiene las columnas de períodos dinámicas basándose en los datos
   */
  getColumnasPeriodos(options) {
    // Si se pasan las columnas de períodos en options, usarlas
    if (options.columnasPeriodos && Array.isArray(options.columnasPeriodos)) {
      console.log(`✅ Info Cobranzas - Usando ${options.columnasPeriodos.length} columnas de períodos del contexto`);
      return options.columnasPeriodos;
    }

    // Si se pasa el contexto con datos, calcularlas
    if (options.datos && Array.isArray(options.datos)) {
      console.log('📊 Info Cobranzas - Calculando columnas de períodos desde datos');
      return this.calcularColumnasPeriodosDesdeDatos(options.datos);
    }

    console.warn('⚠️ Info Cobranzas - No se encontraron columnas de períodos en options ni datos');
    return [];
  }

  /**
   * Calcula las columnas de períodos basándose en los datos reales
   * Los datos ya vienen procesados con propiedades periodo_YYYY-MM
   */
  calcularColumnasPeriodosDesdeDatos(datos) {
    const meses = {
      1: 'ene', 2: 'feb', 3: 'mar', 4: 'abr', 5: 'may', 6: 'jun',
      7: 'jul', 8: 'ago', 9: 'sep', 10: 'oct', 11: 'nov', 12: 'dic'
    };

    const periodosSet = new Set();

    // Detectar períodos desde las propiedades procesadas periodo_YYYY-MM
    datos.forEach(item => {
      if (!item._esSubtotal) {
        // Buscar todas las propiedades que empiecen con "periodo_"
        Object.keys(item).forEach(key => {
          if (key.startsWith('periodo_') && item[key] && item[key] > 0) {
            // Extraer el período YYYY-MM de la clave periodo_YYYY-MM
            const periodo = key.replace('periodo_', '');
            periodosSet.add(periodo);
          }
        });
      }
    });

    const periodosHeaders = [];

    // Generar headers ordenados cronológicamente
    if (periodosSet.size > 0) {
      Array.from(periodosSet)
        .sort()
        .forEach(periodo => {
          // periodo: "2025-11"
          const [año, mesStr] = periodo.split('-');
          const mesNum = parseInt(mesStr, 10);
          const añoCorto = año.slice(-2);

          periodosHeaders.push({
            text: `${meses[mesNum]} ${añoCorto}`,
            value: `periodo_${periodo}`,
            periodo: periodo // Guardar el período para búsquedas
          });
        });
    }

    console.log(`📊 Info Cobranzas - Calculadas ${periodosHeaders.length} columnas de períodos`);
    return periodosHeaders;
  }

  /**
   * Obtiene el monto para un período específico de un item
   * Los datos ya vienen procesados con propiedades periodo_YYYY-MM
   */
  getMontoPorPeriodo(item, periodo) {
    // Acceder directamente a la propiedad procesada periodo_YYYY-MM
    const propertyKey = `periodo_${periodo}`;
    return item[propertyKey] ? parseFloat(item[propertyKey]) : 0;
  }

  /**
   * Obtiene el subtotal para un período específico
   */
  getSubtotalParaPeriodo(item, periodo) {
    if (!item._esSubtotal || !item.subtotales_periodos) {
      return 0;
    }

    // Buscar en el array de subtotales el período específico
    const subtotal = item.subtotales_periodos.find(sub => sub.periodo === periodo);
    return subtotal ? parseFloat(subtotal.total) : 0;
  }

  /**
   * Info Cobranzas no usa desembolsos
   */
  getMaxDesembolsos(options) {
    return 0;
  }
}
